import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Link } from "react-router-dom";
import { Plus, Save, Trash2, Loader2, Languages, ImagePlus, ArrowLeft, ExternalLink, FileText, LibraryBig, X } from "lucide-react";
import { toast } from "sonner";
import {
  mlAdminList, mlAdminGet, mlCreate, mlUpdate, mlDelete, mlUpload, translateFields, fileUrl,
} from "../lib/api";
import { C } from "../components/malayalam/mlShared";
import { MediaLibrary } from "../components/malayalam/MediaLibrary";

const SECTIONS = [
  ["movies", "Movies"], ["meet-the-star", "Meet the Star"], ["interviews", "Interviews"],
  ["cinema-history", "Cinema History"], ["memories", "Memories"], ["gallery", "Gallery"],
  ["news", "News"], ["about-amma", "About AMMA"], ["star-address", "Star Address"],
];
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }], ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }], ["blockquote", "link"], ["clean"],
  ],
};
const blank = (section) => ({
  section, slug: "", status: "draft", title_en: "", title_ml: "", summary_en: "", summary_ml: "",
  body_en: "", body_ml: "", cover_image: "", images: [], category: "", tags: [], date: "", author: "",
});

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide" style={{ color: C.muted }}>{label}</span>
    {children}
  </label>
);
const inputCls = "w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2";
const inputStyle = { borderColor: C.line, background: "#fff", color: C.ink };

export default function MalayalamAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(blank("movies"));
  const [editingId, setEditingId] = useState(null);
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [view, setView] = useState("content");
  const coverRef = useRef(null);
  const galleryRef = useRef(null);

  const load = useCallback(async () => {
    try { const d = await mlAdminList(filter); setItems(d.items || []); }
    catch { toast.error("Could not load content"); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const newDoc = () => { setForm(blank(filter || "movies")); setEditingId(null); setLang("en"); };

  const edit = async (id) => {
    try { const d = await mlAdminGet(id); setForm({ ...blank(d.section), ...d }); setEditingId(id); setLang("en"); window.scrollTo(0, 0); }
    catch { toast.error("Could not open item"); }
  };

  const save = async () => {
    if (!form.title_en && !form.title_ml) { toast.error("Add a title (English or Malayalam)"); return; }
    setBusy(true);
    const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      const saved = editingId ? await mlUpdate(editingId, payload) : await mlCreate(payload);
      setEditingId(saved.id); setForm({ ...blank(saved.section), ...saved });
      toast.success(editingId ? "Saved" : "Created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editingId || !window.confirm("Delete this content?")) return;
    try { await mlDelete(editingId); toast.success("Deleted"); newDoc(); load(); }
    catch { toast.error("Delete failed"); }
  };

  const autoTranslate = async () => {
    if (!form.title_en && !form.summary_en && !form.body_en) { toast.error("Write the English version first"); return; }
    setTranslating(true);
    try {
      const { fields } = await translateFields(
        { title: form.title_en, summary: form.summary_en, body: form.body_en }, "Malayalam", "ml");
      setForm((f) => ({ ...f, title_ml: fields.title || f.title_ml, summary_ml: fields.summary || f.summary_ml, body_ml: fields.body || f.body_ml }));
      setLang("ml");
      toast.success("Translated to Malayalam — please review");
    } catch { toast.error("Translation failed"); }
    finally { setTranslating(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const up = await mlUpload(file); set("cover_image", up.url); toast.success("Image uploaded"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setGalleryUploading(true);
    for (const file of files) {
      try { const up = await mlUpload(file); setForm((f) => ({ ...f, images: [...(f.images || []), up.url] })); }
      catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    }
    setGalleryUploading(false);
    toast.success("Photos added — remember to Save");
  };
  const removeGalleryImage = (u) => setForm((f) => ({ ...f, images: (f.images || []).filter((x) => x !== u) }));

  const L = lang; // active language suffix
  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.ink }} data-testid="ml-admin">
      <header className="border-b" style={{ borderColor: C.line, background: "#FCF8EF" }}>
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/malayalam" className="inline-flex items-center gap-1 text-sm" style={{ color: C.muted }}>
              <ArrowLeft className="h-4 w-4" /> Edition
            </Link>
            <h1 className="text-xl font-bold" style={{ fontFamily: "Georgia, serif", color: C.navy }}>
              MalayalaCinema <span style={{ color: C.gold }}>CMS</span>
            </h1>
            <div className="ml-2 inline-flex rounded-md border p-0.5" style={{ borderColor: C.line }}>
              <button onClick={() => setView("content")} data-testid="ml-admin-tab-content"
                className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-semibold"
                style={{ background: view === "content" ? C.navy : "transparent", color: view === "content" ? "#fff" : C.navy }}>
                <FileText className="h-4 w-4" /> Content
              </button>
              <button onClick={() => setView("media")} data-testid="ml-admin-tab-media"
                className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-semibold"
                style={{ background: view === "media" ? C.navy : "transparent", color: view === "media" ? "#fff" : C.navy }}>
                <LibraryBig className="h-4 w-4" /> Media Library
              </button>
            </div>
          </div>
          {view === "content" && (
            <button onClick={newDoc} data-testid="ml-admin-new"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white"
              style={{ background: C.rust }}>
              <Plus className="h-4 w-4" /> New Content
            </button>
          )}
        </div>
      </header>

      {view === "media" ? (
        <div className="mx-auto max-w-[1400px] px-6 py-6">
          <MediaLibrary />
        </div>
      ) : (
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-[320px_1fr]">
        {/* List */}
        <aside>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className={inputCls} style={inputStyle} data-testid="ml-admin-section-filter">
            <option value="">All sections</option>
            {SECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="mt-3 space-y-2">
            {items.length === 0 && <p className="text-sm" style={{ color: C.muted }}>No content yet.</p>}
            {items.map((it) => (
              <button key={it.id} onClick={() => edit(it.id)} data-testid={`ml-admin-item-${it.id}`}
                className="flex w-full items-center gap-3 rounded-lg border p-2 text-left transition-colors hover:border-gold"
                style={{ borderColor: editingId === it.id ? C.gold : C.line, background: "#fff" }}>
                {it.cover_image
                  ? <img src={fileUrl(it.cover_image)} alt="" className="h-11 w-11 shrink-0 rounded object-cover" />
                  : <span className="h-11 w-11 shrink-0 rounded" style={{ background: C.line }} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold" style={{ color: C.navy }}>{it.title_en || it.title_ml || "(untitled)"}</span>
                  <span className="text-xs" style={{ color: C.muted }}>{it.section} · </span>
                  <span className="text-xs font-semibold" style={{ color: it.status === "published" ? C.teal : C.gold }}>{it.status}</span>
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <main className="rounded-xl border p-5" style={{ borderColor: C.line, background: C.card }} data-testid="ml-admin-editor">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Section">
              <select value={form.section} onChange={(e) => set("section", e.target.value)} className={inputCls} style={inputStyle} data-testid="ml-admin-section">
                {SECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} style={inputStyle} data-testid="ml-admin-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </Field>
            <Field label="Date"><input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} className={inputCls} style={inputStyle} /></Field>
            <Field label="Category"><input value={form.category || ""} onChange={(e) => set("category", e.target.value)} className={inputCls} style={inputStyle} placeholder="e.g. Thriller" /></Field>
          </div>

          {/* Cover image */}
          <div className="mb-4 flex items-center gap-3">
            {form.cover_image
              ? <img src={fileUrl(form.cover_image)} alt="cover" className="h-16 w-24 rounded object-cover" data-testid="ml-admin-cover-preview" />
              : <div className="flex h-16 w-24 items-center justify-center rounded" style={{ background: C.line }}><ImagePlus className="h-5 w-5" style={{ color: C.muted }} /></div>}
            <button onClick={() => coverRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm" style={{ borderColor: C.line, color: C.navy }} data-testid="ml-admin-cover-upload">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Cover image
            </button>
            <input ref={coverRef} type="file" accept="image/*" hidden onChange={uploadCover} />
          </div>

          {/* Gallery photos for this page */}
          <div className="mb-4">
            <div className="mb-2 flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.muted }}>Photos for this page</span>
              <button onClick={() => galleryRef.current?.click()} disabled={galleryUploading}
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold" style={{ borderColor: C.line, color: C.navy }} data-testid="ml-admin-gallery-upload">
                {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Add photos
              </button>
              <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={uploadGallery} />
            </div>
            {(form.images || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="ml-admin-gallery">
                {(form.images || []).map((u) => (
                  <div key={u} className="relative h-20 w-20 overflow-hidden rounded-lg border" style={{ borderColor: C.line }}>
                    <img src={fileUrl(u)} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => removeGalleryImage(u)} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white" data-testid="ml-admin-gallery-remove">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Language tabs */}
          <div className="mb-3 flex items-center justify-between">
            <div className="inline-flex rounded-md border p-0.5" style={{ borderColor: C.line }}>
              <button onClick={() => setLang("en")} data-testid="ml-admin-lang-en"
                className="rounded px-4 py-1.5 text-sm font-semibold" style={{ background: L === "en" ? C.navy : "transparent", color: L === "en" ? "#fff" : C.navy }}>English</button>
              <button onClick={() => setLang("ml")} data-testid="ml-admin-lang-ml"
                className="rounded px-4 py-1.5 text-sm font-semibold" style={{ background: L === "ml" ? C.navy : "transparent", color: L === "ml" ? "#fff" : C.navy }}>മലയാളം</button>
            </div>
            <button onClick={autoTranslate} disabled={translating} data-testid="ml-admin-translate"
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-white" style={{ background: C.teal }}>
              {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />} Auto-translate EN → ML
            </button>
          </div>

          <div className="space-y-3">
            <Field label={`Title (${L === "en" ? "English" : "Malayalam"})`}>
              <input value={form[`title_${L}`] || ""} onChange={(e) => set(`title_${L}`, e.target.value)} className={inputCls} style={inputStyle} data-testid={`ml-admin-title-${L}`} />
            </Field>
            <Field label={`Summary (${L === "en" ? "English" : "Malayalam"})`}>
              <textarea rows={2} value={form[`summary_${L}`] || ""} onChange={(e) => set(`summary_${L}`, e.target.value)} className={inputCls} style={inputStyle} />
            </Field>
            <div>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide" style={{ color: C.muted }}>Body ({L === "en" ? "English" : "Malayalam"})</span>
              <div className={L === "ml" ? "ml-lang-ml bg-white" : "bg-white"} data-testid="ml-admin-body">
                <ReactQuill key={`${editingId || "new"}-${L}`} theme="snow" modules={QUILL_MODULES}
                  value={form[`body_${L}`] || ""} onChange={(v) => set(`body_${L}`, v)} />
              </div>
            </div>
            <Field label="Tags (comma separated)">
              <input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} className={inputCls} style={inputStyle} />
            </Field>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button onClick={save} disabled={busy} data-testid="ml-admin-save"
              className="inline-flex items-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-semibold text-white" style={{ background: C.rust }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {editingId ? "Save changes" : "Create"}
            </button>
            {editingId && form.status === "published" && (
              <Link to={`/malayalam/${form.section}/${form.slug}`} target="_blank"
                className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: C.teal }}>
                <ExternalLink className="h-4 w-4" /> View live
              </Link>
            )}
            {editingId && (
              <button onClick={remove} data-testid="ml-admin-delete"
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-semibold" style={{ borderColor: "#e0b4b4", color: "#b23b3b" }}>
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
          </div>
        </main>
      </div>
      )}
    </div>
  );
}
